import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import {
  ArrowLeft,
  Check,
  MessageCircle,
  Phone,
  ShieldCheck,
  Truck,
  Package,
} from "lucide-react";
import { findProduct, PRODUCTS } from "../lib/products";
import QuoteDialog from "../components/QuoteDialog";
import ProductCard from "../components/ProductCard";

import SEO from "../components/SEO";
export default function ProductDetail() {
  const { slug } = useParams();
  const product = findProduct(slug);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [selectedWeight, setSelectedWeight] = useState("");
  const [selectedColor, setSelectedColor] = useState("");

  useEffect(() => {
    if (!product) return;
    setSelectedWeight(product.variants?.weight?.[0] || "");
    setSelectedColor(product.variants?.color?.[0] || "");
  }, [product]);

  if (!product) return <Navigate to="/products" replace />;

  const related = PRODUCTS.filter(
    (item) => item.slug !== product.slug && item.productCategory === product.productCategory
  ).slice(0, 3);

  let displayImage = product.image;
  if (
    ["knitted-hand-gloves", "knitted-dotted-hand-gloves"].includes(product.slug) &&
    product.gallery?.length >= 2
  ) {
    displayImage = selectedColor === "White" ? product.gallery[1] : product.gallery[0];
  }

  const productForQuote = {
    ...product,
    selectedVariants: {
      ...(selectedWeight ? { weight: selectedWeight } : {}),
      ...(selectedColor ? { color: selectedColor } : {}),
    },
  };

  const isRubber = product.productCategory === "Rubber Hand Gloves";
  const isCotton = product.productCategory === "Cotton Hand Gloves";
  const isCoated = product.productCategory === "Industrial Coated Hand Gloves";

  const seoTitle = isRubber
    ? `${product.name} | Rubber Hand Gloves Manufacturer India`
    : isCotton
      ? `${product.name} | Cotton Hand Gloves Supplier India`
      : isCoated
        ? `${product.name} | Industrial Safety Gloves India`
        : `${product.name} | Hand Gloves India`;

  const seoDescription = isRubber
    ? `${product.name} by RSN Gloves - ${product.tagline}. Made in India rubber hand gloves available for bulk supply to dealers and industrial buyers.`
    : isCotton
      ? `${product.name} - ${product.tagline}. Cotton hand gloves available for bulk industrial and commercial supply across India.`
      : isCoated
        ? `${product.name} - ${product.tagline}. Industrial safety gloves and coated hand protection available for bulk supply across India.`
        : `${product.name} - ${product.tagline}. Hand gloves available for bulk supply across India.`;

  const seoIntro = isRubber
    ? `Made in India rubber hand gloves for industrial, commercial and general-purpose requirements. Available for bulk supply to dealers, distributors and industrial buyers.`
    : isCotton
      ? `Cotton hand gloves for industrial handling, workshops and general-purpose work. Available for bulk supply across India.`
      : isCoated
        ? `Industrial safety gloves for demanding handling applications, with coated hand protection available for bulk supply across India.`
        : `Reliable hand protection available for bulk supply across India.`;

  return (
    <div data-testid="product-detail-page" className="bg-[#FBFCFA] min-h-screen">
      <SEO
        title={seoTitle}
        description={seoDescription}
        path={`/products/${product.slug}`}
        type="product"
        image={
          product.image?.startsWith("http")
            ? product.image
            : `https://www.rsngloves.com${product.image}`
        }
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: product.name,
          description: product.tagline,
          image: product.gallery?.map((img) =>
            img?.startsWith("http") ? img : `https://www.rsngloves.com${img}`
          ),
          brand: {
            "@type": "Brand",
            name: product.brand || "RSN Gloves",
          },
          material: product.material || undefined,
          category: product.productCategory || product.category || "Hand Gloves",
        }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-6">
        <Link
          to={`/products?category=${encodeURIComponent(product.productCategory || "")}`}
          className="inline-flex items-center gap-1.5 text-xs font-bold text-[#5F6B66] hover:text-[#075B3A]"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to {product.productCategory || "Products"}
        </Link>
      </div>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-7 sm:py-10">
        <div className="soft-card p-5 sm:p-7 lg:p-8 grid lg:grid-cols-2 gap-8 lg:gap-12">
          <div>
            <div className="relative aspect-square rounded-lg border border-[#E1E8E3] bg-[#FAFCFA] overflow-hidden">
              <img src={displayImage} alt={product.name} className="w-full h-full object-contain p-6" />
              {product.brand && (
                <span className="absolute top-4 left-4 bg-[#075B3A] text-white text-[10px] font-bold uppercase tracking-[0.18em] px-3 py-1.5 rounded-full">
                  {product.brand}
                </span>
              )}
              {product.cutResistance && (
                <span className="absolute top-4 right-4 bg-[#EAF5EF] border border-[#C7E0D1] text-[#075B3A] text-xs font-black px-3 py-1.5 rounded-full">
                  {product.cutResistance}
                </span>
              )}
            </div>

            {product.gallery?.length > 1 && (
              <div className="flex gap-3 mt-4">
                {product.gallery.map((image, index) => (
                  <button
                    key={image}
                    type="button"
                    onClick={() => {
                      if (product.variants?.color?.[index]) setSelectedColor(product.variants.color[index]);
                    }}
                    className={`w-20 h-20 rounded-md border bg-white overflow-hidden ${
                      displayImage === image ? "border-[#075B3A] ring-1 ring-[#075B3A]" : "border-[#DFE7E2]"
                    }`}
                  >
                    <img src={image} alt="" className="w-full h-full object-contain p-2" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-col justify-center">
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-[#15935F]">
              {product.brand ? `${product.brand} · ` : ""}{product.category}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-black text-[#0F172A] mt-3 leading-[1.02]">
              {product.name}
            </h1>
            <p className="text-base sm:text-lg text-[#5F6B66] mt-4 leading-relaxed">{product.tagline}</p>
            <p className="text-sm sm:text-base text-[#66736C] mt-3 leading-relaxed max-w-xl">
              {seoIntro}
            </p>

            {product.variants && (
              <div className="mt-7 grid sm:grid-cols-2 gap-4">
                {product.variants.weight?.length > 1 && (
                  <VariantSelect
                    label="Weight"
                    value={selectedWeight}
                    options={product.variants.weight}
                    onChange={setSelectedWeight}
                  />
                )}
                {product.variants.color?.length > 1 && (
                  <VariantSelect
                    label="Colour"
                    value={selectedColor}
                    options={product.variants.color}
                    onChange={setSelectedColor}
                  />
                )}
              </div>
            )}

            <div className="mt-7 border-y border-[#E1E8E3] divide-y divide-[#E7EDE9]">
              {product.cutResistance && <InfoRow label="Cut Resistance" value={product.cutResistance} highlight />}
              {product.en388 && <InfoRow label="Standard" value={`EN 388:2016 · ${product.en388}`} />}
              {product.gauge && <InfoRow label="Gauge" value={product.gauge} />}
              {product.material && <InfoRow label="Material" value={product.material} />}
              {product.sizes?.length > 0 && <InfoRow label="Sizes" value={product.sizes.join(", ")} />}
              {product.length && <InfoRow label="Length" value={product.length} />}
              {product.weight && <InfoRow label="Weight" value={selectedWeight || product.weight} />}
            </div>

            <div className="mt-7 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => setQuoteOpen(true)}
                className="btn-primary inline-flex items-center gap-2"
                data-testid="pdp-quote-btn"
              >
                <MessageCircle className="w-4 h-4" /> Request a Quote
              </button>
              <a href="tel:+918928341661" className="btn-secondary inline-flex items-center gap-2">
                <Phone className="w-4 h-4" /> Call Sales
              </a>
            </div>

            <div className="grid sm:grid-cols-3 gap-3 mt-7">
              <Trust icon={ShieldCheck} title="Reliable" text="Protection" />
              <Trust icon={Truck} title="Pan-India" text="Dispatch" />
              <Trust icon={Package} title="Bulk" text="Supply" />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white border-y border-[#DFE7E2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 lg:py-16 grid lg:grid-cols-[280px_1fr] gap-8 lg:gap-12">
          <div>
            <div className="section-kicker">Technical Specification</div>
            <h2 className="font-display text-3xl font-black text-[#0F172A] mt-2">Built for performance.</h2>
            {product.brand && <p className="text-sm text-[#66736C] mt-3">Brand: <strong className="text-[#0F172A]">{product.brand}</strong></p>}
          </div>

          <div className="grid sm:grid-cols-2 gap-px bg-[#DFE7E2] border border-[#DFE7E2] rounded-lg overflow-hidden">
            {product.material && <Spec label="Material" value={product.material} />}
            {product.liner && <Spec label="Liner" value={product.liner} />}
            {product.gauge && <Spec label="Gauge" value={product.gauge} />}
            {product.cutResistance && <Spec label="Cut Resistance" value={product.cutResistance} highlight />}
            {product.en388 && <Spec label="EN 388:2016" value={product.en388} highlight />}
            {product.length && <Spec label="Length" value={product.length} />}
            {product.sizes?.length > 0 && <Spec label="Available Sizes" value={product.sizes.join(", ")} />}
            {product.linerColor && <Spec label="Liner Colour" value={product.linerColor} />}
            {product.coatingColor && <Spec label="Coating Colour" value={product.coatingColor} />}
            {product.coatingStyle?.length > 0 && <Spec label="Coating Style" value={product.coatingStyle.join(", ")} />}
            {!product.linerColor && (selectedColor || product.color) && <Spec label="Colour" value={selectedColor || product.color} />}
            {product.pack && <Spec label="Pack Format" value={product.pack} />}
            {product.applications?.length > 0 && <Spec label="Applications" value={product.applications.join(", ")} />}
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-14">
          <h3 className="font-display text-2xl font-black text-[#0F172A] mb-5">Key Features & Benefits</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {product.features.map((feature) => (
              <div key={feature} className="bg-[#F8FAF8] border border-[#DFE7E2] rounded-lg p-5 flex gap-3">
                <Check className="w-5 h-5 text-[#15935F] shrink-0 mt-0.5" />
                <span className="text-sm font-medium text-[#26332D]">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 lg:py-16">
          <h2 className="font-display text-3xl font-black text-[#0F172A] mb-7">You may also like</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {related.map((item) => <ProductCard key={item.slug} product={item} />)}
          </div>
        </section>
      )}

      <QuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} product={productForQuote} />
    </div>
  );
}

function VariantSelect({ label, value, options, onChange }) {
  return (
    <label className="block">
      <span className="block text-xs font-bold uppercase tracking-[0.15em] text-[#5F6B66] mb-2">{label}</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="w-full h-11 bg-white border border-[#CAD8CF] rounded-md px-3 font-semibold text-sm text-[#0F172A] outline-none focus:border-[#075B3A]"
      >
        {options.map((option) => <option key={option}>{option}</option>)}
      </select>
    </label>
  );
}

function InfoRow({ label, value, highlight = false }) {
  return (
    <div className="grid grid-cols-[130px_1fr] gap-4 py-3 text-sm">
      <span className="text-[#66736C]">{label}</span>
      <span className={`font-semibold ${highlight ? "text-[#075B3A]" : "text-[#0F172A]"}`}>{value}</span>
    </div>
  );
}

function Trust({ icon: Icon, title, text }) {
  return (
    <div className="flex items-center gap-2.5">
      <Icon className="w-5 h-5 text-[#075B3A]" />
      <div className="leading-tight">
        <div className="text-xs font-bold text-[#0F172A]">{title}</div>
        <div className="text-[11px] text-[#66736C]">{text}</div>
      </div>
    </div>
  );
}

function Spec({ label, value, highlight = false }) {
  if (!value) return null;
  return (
    <div className="bg-white p-5">
      <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#708078]">{label}</div>
      <div className={`mt-1.5 font-semibold ${highlight ? "text-[#075B3A] text-lg" : "text-[#0F172A]"}`}>{value}</div>
    </div>
  );
}
